import { useState } from "react";
import { FadeUp } from "./Animations";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";

const REVIEWS = [
  {
    name: "Jason V.",
    date: "March 2025",
    source: "Google",
    text: "Have gotten to know Jaime really well over the years, she is an amazing interior design specialist. The designs she's put together for our business are looking incredible, so excited to see the finished project. I would give her 10 stars if I could.",
  },
  {
    name: "Jacob H.",
    date: "September 2025",
    source: "",
    text: "I had the pleasure of working with Jaime on my townhouse, and I cannot recommend her enough. Her design services transformed my space into something both stylish and comfortable, blending functionality with creativity in a way that perfectly fit my lifestyle. She has an incredible eye for detail and a natural ability to balance colors, textures, and layout.",
  },
  {
    name: "Candice M.",
    date: "July 2024",
    source: "",
    text: "Jamie truly understood my vision and brought it to life with her creative ideas and attention to detail. She was professional, responsive, and always kept me informed. Jamie managed to stay within budget while still delivering high-quality work. The transformation was incredible.",
  },
  {
    name: "Jim B.",
    date: "February 2025",
    source: "",
    text: "I have had the pleasure of working with Jamie for the past five years. Jamie has always been great at envisioning the design of remodel projects from start to finish. Her attention to details is amazing! I would highly recommend Jamie for any remodel or design project.",
  },
  {
    name: "Dixon O.",
    date: "February 2025",
    source: "",
    text: "Jamie and I have worked on many projects over the last five years from bath remodels to kitchen remodels and whole house design. She is a pleasure to work with and has designed beautiful spaces. Her passion for projects shows in the end results.",
  },
  {
    name: "Monica L.",
    date: "July 2024",
    source: "",
    text: "An amazing transformation! Jamie kept us focused on our design and color scheme. What I liked most is that we'd tell her what we liked and she brought samples to choose from. Never once had to step foot in a store. Would use her over and over again!",
  },
  {
    name: "Cory B.",
    date: "September 2023",
    source: "Google",
    text: "I would highly recommend Jamie for your interior design needs. Jamie made it so easy on me that we added the master bedroom to the rest of the project. I couldn't be happier with the results. I will use Jamie again in the future and you should too.",
  },
  {
    name: "Susan J.",
    date: "November 2023",
    source: "Google",
    text: "Jamie was wonderful to work with. She really listened and quickly understood what we needed to warm up our new home while being sensitive to our budget. She has a wonderful eye and I loved all of her design ideas. We are thrilled with the job she did.",
  },
  {
    name: "Samantha A.",
    date: "September 2025",
    source: "Thumbtack",
    text: "Jamie was an absolute pleasure to work with, a true professional, and can make your home look like a dream come true!",
  },
  {
    name: "Larissa K.",
    date: "June 2024",
    source: "",
    text: "Jamie was extremely professional in her approach. She was always punctual and consistently offered fantastic ideas while also taking the time to listen to mine. Her work brought perfect harmony to my home by skillfully integrating my existing furniture with our combined ideas.",
  },
];

export default function Testimonials() {
  const [current, setCurrent] = useState(0);
  const perPage = 3;
  const totalPages = Math.ceil(REVIEWS.length / perPage);
  const visible = REVIEWS.slice(current * perPage, current * perPage + perPage);

  return (
    <section data-testid="testimonials-section" className="bg-onyx py-24 md:py-32 px-6 md:px-16">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between mb-16">
          <div>
            <FadeUp>
              <p className="text-xs tracking-[0.3em] uppercase text-taupe mb-8">
                Client Voices
              </p>
            </FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="font-serif text-3xl md:text-5xl font-light text-bone leading-snug">
                What Our Clients Say
              </h2>
            </FadeUp>
          </div>
          <FadeUp delay={0.2}>
            <div className="hidden md:flex items-center gap-3">
              <button
                data-testid="testimonial-prev"
                onClick={() => setCurrent((p) => (p - 1 + totalPages) % totalPages)}
                className="w-10 h-10 border border-bone/20 flex items-center justify-center text-bone/60 hover:text-bone hover:border-bone/40 transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                data-testid="testimonial-next"
                onClick={() => setCurrent((p) => (p + 1) % totalPages)}
                className="w-10 h-10 border border-bone/20 flex items-center justify-center text-bone/60 hover:text-bone hover:border-bone/40 transition-colors"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </FadeUp>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {visible.map((review, i) => (
            <FadeUp key={`${current}-${i}`} delay={i * 0.12}>
              <div
                data-testid={`testimonial-card-${i}`}
                className="border-t border-bone/10 pt-8 flex flex-col h-full"
              >
                <div className="flex gap-1 mb-6">
                  {[...Array(5)].map((_, j) => (
                    <Star
                      key={j}
                      size={14}
                      className="text-taupe fill-taupe"
                    />
                  ))}
                </div>
                <p className="text-base font-light text-concrete leading-[1.85] flex-1 mb-8">
                  "{review.text}"
                </p>
                <div>
                  <p className="text-sm font-light text-bone">
                    {review.name}
                  </p>
                  <p className="text-xs text-bone/40 mt-1">
                    {review.date}
                    {review.source && ` — via ${review.source}`}
                  </p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>

        {/* Mobile pagination */}
        <div className="flex md:hidden items-center justify-center gap-3 mt-10">
          <button
            onClick={() => setCurrent((p) => (p - 1 + totalPages) % totalPages)}
            className="w-10 h-10 border border-bone/20 flex items-center justify-center text-bone/60"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="text-xs text-bone/40 tracking-widest">
            {current + 1} / {totalPages}
          </span>
          <button
            onClick={() => setCurrent((p) => (p + 1) % totalPages)}
            className="w-10 h-10 border border-bone/20 flex items-center justify-center text-bone/60"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}
